import React, { useState, useEffect } from 'react';
import CaseCreatorLayout from './CaseCreatorLayout';
import { CreateCaseForm, DebugInfoForm } from './forms';
import { CaseHistoryPanel } from './panels';
import { DebugTabs } from './viewers';
import {
  usePlans,
  useCaseForm,
  useCaseCreation,
  useCaseDebugInfo,
  useSlidingPanel,
  useCaseHistory,
} from '../../hooks';
import { ACTION_TYPES } from '../../constants/formConstants';
import './CaseCreator.css';

/**
 * CaseCreator - Main orchestrator component
 *
 * Combines all hooks and subcomponents to provide:
 * - Action type selection (Create case / Debug info)
 * - Case creation form
 * - Debug info viewer with tabs
 * - Case history sliding panel
 */
const CaseCreator = () => {
  // ============================================
  // Hooks
  // ============================================

  // Plans and provider data
  const {
    plans,
    groupIdMap,
    organizationId,
    providerId,
    availableActionTypes,
    getPlanByName,
    isLoading: isLoadingPlans,
  } = usePlans();

  // Organization context for plan field config lookup
  const orgContext = {
    orgGroupId: groupIdMap?.id,
    organizationId,
  };

  // Form state
  const {
    formData,
    customFieldValues,
    handleChange,
    handleCustomFieldChange,
    setField,
    setFormFromHistory,
    resetAfterDebugFetch,
    requiresPatientDetails,
  } = useCaseForm();

  // Case creation
  const {
    isSubmitting: isCreatingCase,
    caseId,
    createCase,
    reset: resetCaseCreation,
  } = useCaseCreation();

  // Debug info
  const {
    fetchDebugInfo,
    isLoading: isFetchingDebug,
    fetchedCaseId,
    caseData,
    patientData,
    providerData,
    organizationData,
    organizationGroupData,
    outcomePlanData,
    taskGraphTemplatesData,
    outcomePlanInstanceData,
    taskGraphInstancesData,
    executionsData,
    nextExecutionTimeData,
    schedulerStatusData,
    interactionRequestsData,
    interactionResponsesData,
  } = useCaseDebugInfo();

  // Sliding panel for case history
  const historyPanel = useSlidingPanel(false);

  // Case history (localStorage-backed, scoped by org context)
  const {
    cases: historyCases,
    selectedIds: selectedCaseIds,
    isLoading: isLoadingHistory,
    saveCase,
    deleteCase,
    deleteCases,
    selectCase,
    selectAll,
    deselectAll,
    getCase,
    triggerCase,
    getCasesForRetrigger,
  } = useCaseHistory(formData.plan, orgContext);

  // Track which case is currently being edited
  const [editingCaseKey, setEditingCaseKey] = useState(null);

  // Error notification state
  const [errorMessage, setErrorMessage] = useState(null);

  // ============================================
  // Effects
  // ============================================

  // Set default action type when available
  useEffect(() => {
    if (availableActionTypes.length > 0 && !formData.atype) {
      setField('atype', availableActionTypes[0]);
    }
  }, [availableActionTypes, formData.atype, setField]);

  // ============================================
  // Handlers
  // ============================================

  /**
   * Handle form submission based on action type
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null); // Clear previous errors

    try {
      if (formData.atype === ACTION_TYPES.CREATE_CASE) {
        const planDetails = getPlanByName(formData.plan);
        const response = await createCase({
          formData,
          customFieldValues,
          planDetails,
          providerId,
          orgContext,
        });

        // Save to history after successful case creation with caseId
        const createdCaseId = response?.id || null;
        saveCase(formData, customFieldValues, true, createdCaseId);
      } else if (formData.atype === ACTION_TYPES.DEBUG_INFO) {
        await fetchDebugInfo(formData.caId);
        resetAfterDebugFetch();
      }
    } catch (err) {
      setErrorMessage(err.message || 'An unexpected error occurred');
    }
  };

  /**
   * Handle action type change
   */
  const handleActionTypeChange = (e) => {
    handleChange(e);
    // Reset state when switching modes
    resetCaseCreation();
    setErrorMessage(null);
  };

  /**
   * Dismiss the case ID banner
   */
  const handleDismissCaseId = () => {
    resetCaseCreation();
  };

  /**
   * Dismiss the error notification
   */
  const handleDismissError = () => {
    setErrorMessage(null);
  };

  // Case history handlers
  const handleSelectCase = (caseKey) => {
    selectCase(caseKey);
  };

  const handleSelectAll = () => {
    selectAll();
  };

  const handleDeselectAll = () => {
    deselectAll();
  };

  /**
   * Handle clicking on a case to populate the form
   */
  const handleEditCase = (caseData) => {
    if (caseData) {
      setFormFromHistory(caseData);
      setEditingCaseKey(caseData.key);
      deselectAll();
      // Reset case ID when loading from history
      resetCaseCreation();
      setErrorMessage(null);
    }
  };

  /**
   * Clear the editing state
   */
  const handleClearEditing = () => {
    setEditingCaseKey(null);
  };

  /**
   * Handle deleting a single case
   */
  const handleDeleteCase = (caseKey) => {
    const caseData = getCase(caseKey);
    const name = caseData ? `${caseData.firstName} ${caseData.lastName}` : 'this case';

    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      deleteCase(caseKey);
    }
  };

  /**
   * Handle re-triggering selected cases
   * Triggers all selected cases via API
   */
  const handleRetriggerSelected = async () => {
    if (selectedCaseIds.length === 0) return;

    const casesToRetrigger = getCasesForRetrigger(selectedCaseIds);
    if (casesToRetrigger.length === 0) return;

    const confirmMessage = casesToRetrigger.length === 1
      ? `Re-trigger case for ${casesToRetrigger[0].firstName} ${casesToRetrigger[0].lastName}?`
      : `Re-trigger ${casesToRetrigger.length} selected cases?`;

    if (!window.confirm(confirmMessage)) return;

    let successCount = 0;
    let failCount = 0;
    const errors = [];

    for (const caseData of casesToRetrigger) {
      try {
        const planDetails = getPlanByName(caseData.formData.plan);
        if (!planDetails) {
          failCount++;
          errors.push(`Plan not found for ${caseData.firstName} ${caseData.lastName}`);
          continue;
        }

        const response = await createCase({
          formData: caseData.formData,
          customFieldValues: caseData.customFieldValues,
          planDetails,
          providerId,
          orgContext,
        });

        // Update the case's triggeredAt timestamp and caseId
        const newCaseId = response?.id || null;
        triggerCase(caseData.key, newCaseId);
        successCount++;
      } catch (err) {
        console.error(`Failed to re-trigger case ${caseData.key}:`, err);
        failCount++;
        errors.push(`${caseData.firstName} ${caseData.lastName}: ${err.message}`);
      }
    }

    // Show result
    if (failCount === 0) {
      // All succeeded - no notification needed, success is visible in UI
    } else if (successCount === 0) {
      setErrorMessage(`Failed to re-trigger ${failCount} case(s).\n${errors.join('\n')}`);
    } else {
      setErrorMessage(`Re-triggered ${successCount} case(s). ${failCount} failed.\n${errors.join('\n')}`);
    }

    deselectAll();
  };

  /**
   * Handle bulk delete of selected cases
   */
  const handleDeleteSelected = () => {
    if (selectedCaseIds.length === 0) return;

    const confirmMessage = selectedCaseIds.length === 1
      ? 'Are you sure you want to delete this case?'
      : `Are you sure you want to delete ${selectedCaseIds.length} cases?`;

    if (window.confirm(confirmMessage)) {
      deleteCases(selectedCaseIds);
    }
  };

  /**
   * Handle saving case for later (without triggering)
   */
  const handleSaveCase = () => {
    if (!formData.plan) return;
    saveCase(formData, customFieldValues, false);
  };

  // ============================================
  // Computed Values
  // ============================================

  const isCreateCaseMode = formData.atype === ACTION_TYPES.CREATE_CASE;
  const isDebugMode = formData.atype === ACTION_TYPES.DEBUG_INFO;
  const isSubmitting = isCreatingCase || isFetchingDebug;
  const groupName = groupIdMap?.name || '';
  const showDebugTabs = isDebugMode && fetchedCaseId;

  // Layout class for wider debug view - apply whenever in debug mode
  const layoutClass = isDebugMode ? 'case-creator-layout--wide' : '';

  // ============================================
  // Render
  // ============================================

  return (
    <CaseCreatorLayout className={layoutClass}>
      <CaseCreatorLayout.Form>
        {/* Action Type Selector */}
        <div className="case-creator-action-selector">
          <label>
            Select the Jarvis function using the dropdown:
            <select
              name="atype"
              value={formData.atype}
              onChange={handleActionTypeChange}
              required
            >
              <option value="">Select the JARVIS function</option>
              {availableActionTypes.map((atype, index) => (
                <option key={index} value={atype}>
                  {atype}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Border-attached Case History Tab Trigger - Hidden when panel is open */}
        {isCreateCaseMode && formData.plan && !historyPanel.isOpen && (
          <button
            type="button"
            className="case-creator-edge-trigger"
            onClick={historyPanel.open}
            aria-label="Open case history"
            aria-expanded={false}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 4V10L13 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="10" cy="10" r="7" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
            <span className="case-creator-edge-trigger-label">History</span>
          </button>
        )}

        {/* Save Button - Top Right Corner */}
        {isCreateCaseMode && formData.plan && (
          <button
            type="button"
            className="case-creator-save-btn"
            onClick={handleSaveCase}
            disabled={isSubmitting}
            title="Save for later"
            aria-label="Save for later"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
              <polyline points="17 21 17 13 7 13 7 21"/>
              <polyline points="7 3 7 8 15 8"/>
            </svg>
          </button>
        )}

        {/* Create Case Form */}
        {isCreateCaseMode && (
          <CreateCaseForm
            formData={formData}
            customFieldValues={customFieldValues}
            onChange={handleChange}
            onCustomFieldChange={handleCustomFieldChange}
            onSubmit={handleSubmit}
            onDismissCaseId={handleDismissCaseId}
            plans={plans}
            isLoadingPlans={isLoadingPlans}
            groupName={groupName}
            orgContext={orgContext}
            isSubmitting={isSubmitting}
            caseId={caseId}
            errorMessage={errorMessage}
            onDismissError={handleDismissError}
            requiresPatientDetails={requiresPatientDetails}
          />
        )}

        {/* Debug Info Form - Always visible in debug mode */}
        {isDebugMode && (
          <DebugInfoForm
            formData={formData}
            onChange={handleChange}
            onSubmit={handleSubmit}
            groupName={groupName}
            fetchedCaseId={fetchedCaseId}
            isSubmitting={isSubmitting}
          />
        )}

        {/* Debug Tabs (shown after fetch) */}
        {showDebugTabs && (
          <DebugTabs
            caseData={caseData}
            patientData={patientData}
            providerData={providerData}
            organizationData={organizationData}
            organizationGroupData={organizationGroupData}
            outcomePlanData={outcomePlanData}
            taskGraphTemplatesData={taskGraphTemplatesData}
            outcomePlanInstanceData={outcomePlanInstanceData}
            taskGraphInstancesData={taskGraphInstancesData}
            executionsData={executionsData}
            nextExecutionTimeData={nextExecutionTimeData}
            schedulerStatusData={schedulerStatusData}
            interactionRequestsData={interactionRequestsData}
            interactionResponsesData={interactionResponsesData}
          />
        )}

        {/* Case History Drawer - Attached below the form card */}
        {isCreateCaseMode && formData.plan && (
          <CaseHistoryPanel
            isOpen={historyPanel.isOpen}
            onClose={historyPanel.close}
            cases={historyCases}
            selectedIds={selectedCaseIds}
            editingCaseKey={editingCaseKey}
            planName={formData.plan}
            isLoading={isLoadingHistory}
            onSelectCase={handleSelectCase}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onEditCase={handleEditCase}
            onDeleteCase={handleDeleteCase}
            onRetriggerSelected={handleRetriggerSelected}
            onDeleteSelected={handleDeleteSelected}
            onClearEditing={handleClearEditing}
          />
        )}
      </CaseCreatorLayout.Form>
    </CaseCreatorLayout>
  );
};

export default CaseCreator;
